/// <reference types="@types/dom-mediacapture-transform" />
import { Results, SelfieSegmentation } from '@mediapipe/selfie_segmentation';
import { Store } from '@ngrx/store';
import { filter, ReplaySubject, takeUntil } from 'rxjs';
import { setTimeout } from 'worker-timers';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { AppState, Timeout } from 'app/types';
import assertNever from 'app/utils/assert-never';
import { Loggable } from 'app/utils/logging/loggable';
import { DUMMY_VIDEO_STREAM } from 'app/utils/media/stream';
import { notUndefined } from 'app/utils/stream-util';

import { PLAYER_SIZE } from '../../constants';
import { socialAppSelectors, VideoEffect, VideoEffectsMode } from '../../state/app';
import { BrowserType } from '../browser.service';

import { doesBrowserSupportTrackProcessor, doesBrowserSupportVideoEffects, getBrowserEffectFPS } from './utils';

enum VideoEffectsMethod {
  TRACK_PROCESSOR = 'TRACK_PROCESSOR',
  CANVAS = 'CANVAS',
}

export class VideoEffectsProcessor extends Loggable {
  private areEffectsEnabled = false;
  private backgroundImage = document.createElement('img');
  private complete = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private mode = VideoEffectsMethod.CANVAS;
  private resultCanvas: HTMLCanvasElement | null = document.createElement('canvas');
  private segmentationResults?: Results;
  private selfieSegmentation: SelfieSegmentation;
  private sendFrameTimeout?: Timeout;
  private sourceCanvas: HTMLCanvasElement | null = document.createElement('canvas');
  private videoStream?: MediaStream;
  private effectFPS = 18;
  private sourceVideo: HTMLVideoElement | null = null;

  public constructor(
    private browserType: BrowserType,
    private store: Store<AppState>,
    private videoEffect: VideoEffect = { enabled: false, mode: VideoEffectsMode.NONE },
  ) {
    super();
    this.resultCanvas!.id = 'resultCanvas';
    this.sourceCanvas!.id = 'sourceCanvas';
    this.setDisplayName('VideoProcessor');
    this.effectFPS = getBrowserEffectFPS(this.browserType);

    this.mode = doesBrowserSupportTrackProcessor(this.browserType)
      ? VideoEffectsMethod.TRACK_PROCESSOR
      : VideoEffectsMethod.CANVAS;

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.video_background_blur)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (areEffectsEnabled) =>
          (this.areEffectsEnabled = areEffectsEnabled && doesBrowserSupportVideoEffects(this.browserType)),
      );

    this.selfieSegmentation = new SelfieSegmentation({
      locateFile: (file) => `dist/assets/selfie_segmentation/${file}`,
    });
    this.selfieSegmentation.setOptions({
      modelSelection: 1,
    });
    this.selfieSegmentation.onResults((results) => {
      this.onSegmentationResults(results);
    });

    if (this.browserType === BrowserType.FIREFOX) {
      // Fix firefox bug where captureStream throws an error if the canvas context has not been used before
      this.resultCanvas?.getContext('2d');
    }

    // Customer API Video FPS
    this.store
      .select(socialAppSelectors.getGuestVideoFPS)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((fps) => {
        this.effectFPS = fps;
      });
  }

  public setEffects(effects: VideoEffect): Promise<void> {
    const complete = new Promise<void>((resolve) => {
      if (effects.mode === VideoEffectsMode.BACKGROUND_IMAGE) {
        this.backgroundImage.onload = () => {
          this.videoEffect = effects;
          resolve();
        };
        this.backgroundImage.src = effects.image;
      } else {
        this.videoEffect = effects;
        resolve();
      }
    });
    return complete;
  }

  /**
   * Process a new video stream, applying video effects to it
   *
   * @param videoStream The video stream to process
   * @return A video stream with effects applied, or the same video stream if no effects are enabled
   */
  public processVideoStream(videoStream: MediaStream): MediaStream {
    if (!this.areEffectsEnabled) {
      return videoStream;
    }
    // If the video stream is the dummy stream, return the same stream. There's nothing to do, and the fake media stream
    // does not support transformation anyway.
    if (videoStream.id === DUMMY_VIDEO_STREAM.id) {
      return videoStream;
    }
    if (!doesBrowserSupportVideoEffects(this.browserType)) {
      return videoStream;
    }

    const { height = 220, width = 220 } = videoStream.getVideoTracks()[0].getSettings();

    this.setContentSize({ width, height });

    this.videoStream = videoStream;

    switch (this.mode) {
      case VideoEffectsMethod.TRACK_PROCESSOR: {
        this.info('Using track processor video effects', { streamId: videoStream.id });
        const [track] = videoStream.getVideoTracks();
        const processor = new MediaStreamTrackProcessor({ track: track as MediaStreamVideoTrack });
        const generator = new MediaStreamTrackGenerator({ kind: 'video' });

        const transformStream = new TransformStream({
          transform: this.transform.bind(this),
        });

        // Transformation pipeline
        processor.readable
          .pipeThrough(transformStream)
          .pipeTo(generator.writable)
          .catch((error: Error) => {
            this.error(`Error processing video stream ${videoStream.id}`, error);
          });

        const modifiedStream = new MediaStream([generator]);
        return modifiedStream;
      }

      case VideoEffectsMethod.CANVAS: {
        this.info('Using canvas video effects', { streamId: videoStream.id });
        this.sourceVideo = document.createElement('video');
        this.sourceVideo.srcObject = videoStream;
        let lastTime = 0;
        const sourceContext = this.sourceCanvas?.getContext('2d', { willReadFrequently: true });
        const sendFrame = async (streamId: string) => {
          if (streamId !== this.videoStream?.id || !sourceContext) {
            return;
          }
          if (this.complete) {
            return;
          }
          const now = this.sourceVideo?.currentTime || 0;
          if (now > lastTime && this.sourceCanvas) {
            // Draw the source content into the source canvas to be used after segmentation
            if (!this.sourceVideo) {
              return;
            }
            sourceContext.drawImage(this.sourceVideo, 0, 0, width, height);

            const image = this.sourceCanvas;
            await this.selfieSegmentation.send({ image });
          }
          lastTime = now;

          setTimeout(() => sendFrame(streamId), 1000 / this.effectFPS);
        };
        this.sourceVideo.play().catch((error) => {
          this.error('Error playing video', error);
        });
        this.sourceVideo.addEventListener('playing', () => sendFrame(videoStream.id));
        return this.resultCanvas!.captureStream();
      }
      default:
        assertNever(this.mode);
    }
  }

  /**
   * Destroy the video effects processor
   */
  public destroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.complete = true;

    if (this.sendFrameTimeout) {
      clearTimeout(this.sendFrameTimeout);
    }
    this.sendFrameTimeout = undefined;
    this.selfieSegmentation.close().catch((error) => {
      this.error('Error closing selfie segmentation', error);
    });
    if (this.sourceVideo) {
      this.sourceVideo.srcObject = null;
    }
    // this.sourceVideo.removeEventListener('playing', sendFrame);
    this.videoStream = undefined;
    this.resultCanvas?.remove();
    this.sourceCanvas?.remove();
    this.resultCanvas = null;
    this.sourceCanvas = null;
  }

  private get isEnabled(): boolean {
    return this.areEffectsEnabled && !!this.videoEffect?.enabled;
  }

  /**
   * Transform a frame of video into a frame of video with effects applied.
   * The modified frame is enqueued for rendering into the media pipeline.
   */
  private async transform(frame: VideoFrame, controller: TransformStreamDefaultController<unknown>): Promise<void> {
    try {
      if (this.complete) {
        frame.close();
        return;
      }
      const { resultCanvas: canvas } = this;
      const canvasContext = canvas?.getContext('2d', { willReadFrequently: true });
      const sourceCanvasContext = this.sourceCanvas?.getContext('2d');
      if (!canvasContext || !sourceCanvasContext) {
        frame.close();
        throw new Error('canvas context not found');
      }

      sourceCanvasContext.drawImage(frame, 0, 0);

      if (this.isEnabled && this.sourceCanvas) {
        const image = this.sourceCanvas;
        await this.selfieSegmentation.send({ image });
      }

      if (!this.segmentationResults || !this.isEnabled) {
        controller.enqueue(frame);
        return;
      }

      if (!this.resultCanvas) {
        throw new Error('resultCanvas is null');
      }
      const { height, width } = this.resultCanvas;
      this.drawEffect(canvasContext, frame, width, height);

      const newFrame = new VideoFrame(this.resultCanvas, { timestamp: 0 });
      controller.enqueue(newFrame);
      frame.close();
    } catch (error) {
      this.error('Error transforming video frame', error);
      frame.close();
    }
  }

  private drawEffect(
    canvasContext: CanvasRenderingContext2D,
    backgroundImage: VideoFrame | HTMLCanvasElement | OffscreenCanvas,
    width: number,
    height: number,
  ) {
    if (!this.segmentationResults) {
      return;
    }
    const { image: segmentationImage, segmentationMask } = this.segmentationResults;

    canvasContext.save();
    canvasContext.clearRect(0, 0, width, height);
    // Draw the mask to the canvas for effects application
    canvasContext.drawImage(segmentationMask, 0, 0, width, height);

    const { mode } = this.videoEffect;

    // Effects are drawn from the mask outward, on the 'background' of the video
    canvasContext.globalCompositeOperation = 'source-out';
    switch (mode) {
      case VideoEffectsMode.BLUR:
        {
          // Scale the blur radius based on the size of the video. This keeps the amount of impact to the background
          // consistent across different video sizes.
          const blurScale = height / PLAYER_SIZE;
          const blurRadius = this.videoEffect.blurRadius * blurScale;
          // Blur the content outside of the mask
          canvasContext.filter = `blur(${blurRadius}px)`;
          canvasContext.drawImage(backgroundImage, 0, 0, width, height);
        }
        break;

      case VideoEffectsMode.NONE:
        canvasContext.filter = '';
        canvasContext.drawImage(backgroundImage, 0, 0, width, height);
        break;

      case VideoEffectsMode.BACKGROUND_IMAGE:
        canvasContext.filter = '';
        // FIXME: Do some work to center the background image within the canvas,
        // instead of just drawing it at the top left
        canvasContext.drawImage(this.backgroundImage, 0, 0, width, height);
        break;

      case VideoEffectsMode.BACKGROUND_COLOR:
        canvasContext.beginPath();
        canvasContext.rect(0, 0, width, height);
        canvasContext.fillStyle = '#123456';
        canvasContext.fill();
        break;

      default:
        assertNever(mode);
    }

    // The result from segmentation is drawn atop the mask
    canvasContext.filter = 'none';
    canvasContext.globalCompositeOperation = 'destination-atop';
    canvasContext.drawImage(segmentationImage, 0, 0, width, height);

    canvasContext.restore();
  }

  /**
   * Set the content size of the source and result canvas.
   */
  private setContentSize({ height, width }: { width: number; height: number }): void {
    this.sourceCanvas!.height = height;
    this.sourceCanvas!.width = width;
    this.resultCanvas!.height = height;
    this.resultCanvas!.width = width;
  }

  private onSegmentationResults(results: Results): void {
    this.segmentationResults = results;
    if (this.mode !== VideoEffectsMethod.CANVAS) {
      return;
    }
    const resultContext = this.resultCanvas?.getContext('2d');
    if (!resultContext) {
      return;
    }
    if (this.sourceCanvas && this.resultCanvas) {
      this.drawEffect(resultContext, this.sourceCanvas, this.resultCanvas.width, this.resultCanvas.height);
    }
  }
}
